
import React, { useEffect, useState } from 'react';
import ImageUpload from '../../../sharedcomponents/imageupload/imageupload';
import { fetchApi } from '../../../services/api';
import { buildD5Payload } from '../../../services/payloads/d5Payload';
import { getEightDInfoId } from '../../../services/eightDInfoStore';
import { getCurrentUserEmail } from '../../../services/auth';
import '../d1/d1.css';
import '../d2/d2.css';
import '../d5/d5.css';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import TwoMonthDatePicker from '../../../sharedcomponents/two-month-datepicker/TwoMonthDatePicker';

// -----------------------------
// Helpers
// -----------------------------
const toIsoOrNull = (date) => {
  if (!date) return null;
  try {
    const d = date instanceof Date ? date : new Date(date);
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
};

const D5 = ({ expanded, onSetClick, handleCloseForm, onToggleAccordion, isEnabled = false, onSaved, eightDInfoId, currentUserEmail = getCurrentUserEmail() || sessionStorage.getItem('userEmail') || '', teamMembers = [], d5Data, d5ResponsibleData, mode, readOnly = false }) => {
  // When readOnly is true, disable all edit interactions
  const effectiveEnabled = isEnabled && !readOnly;
  const canView = isEnabled;
  // Header states
  const [responsiblePerson, setResponsiblePerson] = useState('');
  const [plannedDate, setPlannedDate] = useState(null);
  const [achievedDate, setAchievedDate] = useState(null);
  const [errors, setErrors] = useState({ person: '', planned: '', achieved: '' });

  // D5 tables: Occurrence & Non Detection
  const [occurrenceRows, setOccurrenceRows] = useState([
    { rootCause: '', action: '', responsible: '', date: null, status: '' }
  ]);
  const [nonDetectionRows, setNonDetectionRows] = useState([
    { rootCause: '', action: '', responsible: '', date: null, status: '' }
  ]);

  // Attachments & removal arrays
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [attachmentsToRemove, setAttachmentsToRemove] = useState([]);
  const [occurrenceToRemove, setOccurrenceToRemove] = useState([]);
  const [nonDetectionToRemove, setNonDetectionToRemove] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);

  // Save state & notices
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveNotice, setSaveNotice] = useState(null);
  const [d5Id, setD5Id] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [formSaved, setFormSaved] = useState(false);
  const [isResponsibleSet, setIsResponsibleSet] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAchievedDate, setShowAchievedDate] = useState(false);
  const [formClosed, setFormClosed] = useState(true);

  // Determine if current user is the responsible person for this form
  const isCurrentUserResponsible = responsiblePerson && currentUserEmail && 
    responsiblePerson.toLowerCase() === currentUserEmail.toLowerCase();
  const isFormBlocked = readOnly ? !isCurrentUserResponsible : false;

  // Check if responsible person is already set (only when d5Id exists, meaning Set was clicked)
  useEffect(() => {
    if (d5Id) {
      setIsResponsibleSet(true);
    }
  }, [d5Id]);

  // Fetch D5 data on mount if eightDInfoId exists
  useEffect(() => {
    if (dataLoaded) return;
    
    if (eightDInfoId && !d5Data && !d5ResponsibleData && mode === 'edit') {
      const fetchD5Data = async () => {
        try {
          
          const response = await fetchApi('/api/D5/GetD5', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ EightDInfoId: Number(eightDInfoId) }),
          });
          
          
          if (response?.data) {
            const d5 = response.data;
            
            if (d5.D5Id || d5.d5Id) { setD5Id(d5.D5Id || d5.d5Id); setFormSaved(true); }
            if (d5.ResponsiblePerson) {
              setResponsiblePerson(d5.ResponsiblePerson);
            }
            if (d5.PlannedDate) {
              setPlannedDate(new Date(d5.PlannedDate));
            }
            if (d5.AchievedDate) {
              setAchievedDate(new Date(d5.AchievedDate));
            }
            
            if (d5.OccurrenceResult && Array.isArray(d5.OccurrenceResult) && d5.OccurrenceResult.length > 0) {
              const mapped = d5.OccurrenceResult.map(item => ({
                id: item.D5OccurrenceId || item.d5OccurrenceId || item.Id || item.id || null,
                rootCause: item.RootCause || '',
                action: item.Action || '',
                responsible: item.Responsible || '',
                date: item.TargetDate ? new Date(item.TargetDate) : null,
                status: item.Status || ''
              }));
              setOccurrenceRows(mapped);
            }
            
            if (d5.NonDetectionDetails && Array.isArray(d5.NonDetectionDetails) && d5.NonDetectionDetails.length > 0) {
              const mapped = d5.NonDetectionDetails.map(item => ({
                id: item.D5NonDetectionId || item.d5NonDetectionId || item.Id || item.id || null,
                rootCause: item.RootCause || '',
                action: item.Action || '',
                responsible: item.Responsible || '',
                date: item.TargetDate ? new Date(item.TargetDate) : null,
                status: item.Status || ''
              }));
              setNonDetectionRows(mapped);
            }
            
            if (d5.Attachments && Array.isArray(d5.Attachments)) {
              setUploadedFiles(d5.Attachments);
            }
            setDataLoaded(true);
          }
        } catch (error) {
        }
      };
      fetchD5Data();
      return;
    }
    
    // Handle props data
    if (d5Data) {
      const data = Array.isArray(d5Data) ? d5Data[0] : d5Data;
      if (data) {
        if (data.D5Id || data.d5Id) { setD5Id(data.D5Id || data.d5Id); setFormSaved(true); }
        if (data.OccurrenceResult && Array.isArray(data.OccurrenceResult) && data.OccurrenceResult.length > 0) {
          const mapped = data.OccurrenceResult.map(item => ({
            id: item.D5OccurrenceId || item.d5OccurrenceId || item.Id || item.id || null,
            rootCause: item.RootCause || '',
            action: item.Action || '',
            responsible: item.Responsible || '',
            date: item.TargetDate ? new Date(item.TargetDate) : null,
            status: item.Status || ''
          }));
          setOccurrenceRows(mapped);
        }
        
        if (data.NonDetectionDetails && Array.isArray(data.NonDetectionDetails) && data.NonDetectionDetails.length > 0) {
          const mapped = data.NonDetectionDetails.map(item => ({
            id: item.D5NonDetectionId || item.d5NonDetectionId || item.Id || item.id || null,
            rootCause: item.RootCause || '',
            action: item.Action || '',
            responsible: item.Responsible || '',
            date: item.TargetDate ? new Date(item.TargetDate) : null,
            status: item.Status || ''
          }));
          setNonDetectionRows(mapped);
        }
        
        if (data.Attachments && Array.isArray(data.Attachments)) {
          setUploadedFiles(data.Attachments);
        }
        setDataLoaded(true);
      }
    }
    
    if (d5ResponsibleData) {
      const respData = Array.isArray(d5ResponsibleData) ? d5ResponsibleData[0] : d5ResponsibleData;
      if (respData) {
        setResponsiblePerson(respData.ResponsiblePerson || '');
        setPlannedDate(respData.PlannedDate ? new Date(respData.PlannedDate) : null);
        setAchievedDate(respData.AchievedDate ? new Date(respData.AchievedDate) : null);
      }
    }
  }, [eightDInfoId, d5Data, d5ResponsibleData]);

  const handleRowChange = (rows, setRows, index, field, value) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
    setIsFormTouched(true);
  };
  const maxRows = 3;
  const handleAddRow = (rows, setRows) => {
    setRows((prev) => prev.length < maxRows ? [
      ...prev,
      { rootCause: '', action: '', responsible: '', date: null, status: '' }
    ] : prev);
    setIsFormTouched(true);
  };
  const handleRemoveRow = (rows, setRows, index) => {
    const removed = rows[index];
    if (removed?.id) {
      if (setRows === setOccurrenceRows) {
        setOccurrenceToRemove(prev => [...prev, removed.id]);
      } else {
        setNonDetectionToRemove(prev => [...prev, removed.id]);
      }
    }
    setRows((prev) => prev.filter((_, i) => i !== index));
    setIsFormTouched(true);
  };

  const validateField = (field, value) => {
    let error = '';
    if (field === 'person' && !value) error = 'Responsible person is required';
    if (field === 'planned' && !value) error = 'Planned date is required';
    if (field === 'achieved' && !value) error = 'Achieved date is required';
    setErrors((prev) => ({ ...prev, [field]: error }));
  };
  const handlePersonChange = (value) => {
    setResponsiblePerson(value);
    validateField('person', value);
  };
  const handlePlannedChange = (value) => {
    setPlannedDate(value);
    validateField('planned', value);
  };
  const handleAchievedChange = (value) => {
    setAchievedDate(value);
    validateField('achieved', value);
  };


  const handleUpdateResponsiblePerson = async () => {
    if (!responsiblePerson) return;
    
    const currentEightDInfoId = eightDInfoId || getEightDInfoId();
    setSaveError(null);
    setSaveNotice(null);

    if (!currentEightDInfoId) {
      setSaveError('eightDInfoId is missing.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        D5Id: d5Id || undefined,
        EightDInfoId: currentEightDInfoId,
        ResponsiblePerson: responsiblePerson,
        PlannedDate: plannedDate?.toISOString(),
        AchievedDate: achievedDate?.toISOString(),
        OccurrenceResult: [],
        NonDetectionDetails: [],
        Attachments: [],
        OccurrenceToRemove: [],
        NonDetectionToRemove: [],
        AttachmentsToRemove: [],
        IsDisabled: false,
        CreatedBy: currentUserEmail,
        CreatedOn: new Date().toISOString(),
        ModifiedBy: currentUserEmail,
        ModifiedOn: new Date().toISOString()
      };

      const endpoint = d5Id ? '/api/D5/UpdateD5Responsible' : '/api/D5/CreateD5';
      const response = await fetchApi(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response?.data?.D5Id || response?.data?.d5Id) {
        setD5Id(response.data.D5Id || response.data.d5Id);
      }

      setIsResponsibleSet(true);
      setFormClosed(false);
      setSaveNotice(d5Id ? "Updated D5 form successfully." : "D5 responsible person set successfully.");
      setTimeout(() => setSaveNotice(null), 3000);
    } catch (e) {
      setSaveError(e?.message ?? 'Failed to set responsible person.');
      setTimeout(() => setSaveError(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const currentEightDInfoId = eightDInfoId || getEightDInfoId();
    
    setSaveError(null);
    setSaveNotice(null);

    if (!currentEightDInfoId) {
      setSaveError('EightDInfoId is missing.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    if (!currentUserEmail) {
      setSaveError('Current user email is missing.');
      setTimeout(() => setSaveError(null), 2000);
      return;
    }

    setSaving(true);
    
    try {
      const payload = buildD5Payload({
        eightDInfoId: currentEightDInfoId,
        responsiblePerson,
        plannedDate,
        achievedDate,
        occurrenceRows,
        nonDetectionRows,
        uploadedFiles,
        isDisabled,
        attachmentsToRemove,
        occurrenceToRemove,
        nonDetectionToRemove,
        currentUserEmail,
        d5Id,
      });
      

      
      const endpoint = d5Id ? '/api/D5/UpdateD5' : '/api/D5/CreateD5';

      
      let response;
      try {
        response = await fetchApi(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } catch (fetchError) {
        // API returns 400 but data gets saved â€” treat as success
        if (fetchError?.message?.includes('400')) {
          console.warn('[D5 Save] API returned 400 but data may be saved, proceeding.');
          response = {};
        } else {
          throw fetchError;
        }
      }
      
      if (response?.data?.D5Id || response?.data?.d5Id) {
        setD5Id(response.data.D5Id || response.data.d5Id);
      }

      // Re-fetch to get assigned child IDs
      try {
        const refreshed = await fetchApi('/api/D5/GetD5', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ EightDInfoId: Number(currentEightDInfoId) }),
        });
        if (refreshed?.data) {
          const d5 = refreshed.data;
          if (d5.OccurrenceResult?.length > 0) {
            setOccurrenceRows(d5.OccurrenceResult.map(item => ({
              id: item.D5OccurrenceId || null,
              rootCause: item.RootCause || '', action: item.Action || '',
              responsible: item.Responsible || '', date: item.TargetDate ? new Date(item.TargetDate) : null, status: item.Status || ''
            })));
          }
          if (d5.NonDetectionDetails?.length > 0) {
            setNonDetectionRows(d5.NonDetectionDetails.map(item => ({
              id: item.D5NonDetectionId || null,
              rootCause: item.RootCause || '', action: item.Action || '',
              responsible: item.Responsible || '', date: item.TargetDate ? new Date(item.TargetDate) : null, status: item.Status || ''
            })));
          }
          if (d5.Attachments?.length > 0) setUploadedFiles(d5.Attachments);
        }
      } catch (e) { }

      setOccurrenceToRemove([]);
      setNonDetectionToRemove([]);
      setAttachmentsToRemove([]);
      setSaveNotice('D5 permanent corrective actions saved successfully.');
      setTimeout(() => setSaveNotice(null), 2000);
      setFormSaved(true);
      
      if (onSaved) {
        onSaved(response);
      }
    } catch (error) {
      console.error('[D5 Save] Error:', error);
      setSaveError(error?.message || 'Failed to save D5 permanent corrective actions.');
      setTimeout(() => setSaveError(null), 2000);
    } finally {
      setSaving(false);
    }
  };

  const toggleAccordion = () => {
    if (readOnly && isEnabled) {
      onToggleAccordion();
      return;
    }
    if (effectiveEnabled && isEditMode && (d5Id || (responsiblePerson && plannedDate))) {
      onToggleAccordion();
    }
  };

  const handleReset = () => {
    setOccurrenceRows([{ rootCause: '', action: '', responsible: '', date: null, status: '' }]);
    setNonDetectionRows([{ rootCause: '', action: '', responsible: '', date: null, status: '' }]);
    setUploadedFiles([]);
    setAttachmentsToRemove([]);
    setOccurrenceToRemove([]);
    setNonDetectionToRemove([]);
    setSaveError(null);
    setSaveNotice(null);
  };

  const rowHasInput = (row) => !!(row.rootCause?.trim() || row.action?.trim() || row.responsible || row.date);

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <>
      <Box className={`d3-header ${canView ? 'enabled' : 'disabled'}`} onClick={toggleAccordion} style={{ cursor: canView ? 'pointer' : 'not-allowed', pointerEvents: canView ? 'auto' : 'none', opacity: canView ? 1 : 0.5 }}>
        {/* Left Section */}
        <Box className="d3-left-section">
          <Box className="d3-circle" style={achievedDate && formSaved ? { backgroundColor: '#4ccb91', borderColor: '#4ccb91' } : (responsiblePerson || isEditMode || formSaved ? { backgroundColor: '#f8c239', borderColor: '#f8c239' } : {})} />
          <span className="d3-title">D5</span>
          <span className="d3-subtitle">Develop Permanent Corrective Action/Solution</span>

          {/* Tooltip */}
          <Tooltip
            title={
              <span>
                Propose and plan permanent corrective actions to address the confirmed root causes.
              </span>
            }
            arrow
            placement="bottom-start"
            componentsProps={{
              tooltip: { className: 'custom-tooltip' },
              arrow: { className: 'custom-arrow' }
            }}
          >
            <IconButton>
              <InfoOutlinedIcon sx={{ color: '#666' }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Responsible Person */}
        <Box className="responsible-box" onClick={(e) => e.stopPropagation()}>
          <Select
            value={responsiblePerson}
            onChange={(e) => handlePersonChange(e.target.value)}
            displayEmpty
            variant="standard"
            className="responsible-select"
            error={!!errors.person}
            disabled={!effectiveEnabled || isFormBlocked}
            sx={{
              '& .MuiInput-underline:after': {
                borderBottomColor: '#800040',
              },
              '& .MuiInput-underline:before': {
                borderBottomColor: '#ccc',
              },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                borderBottomColor: '#800040',
              }
            }}
          >
            <MenuItem value="" sx={{ display: 'none' }}>Responsible Person</MenuItem>
            {responsiblePerson && !teamMembers.some(m => (m.EmailId || m.emailId) === responsiblePerson) && (
              <MenuItem value={responsiblePerson}>{responsiblePerson}</MenuItem>
            )}
            {teamMembers.map((member) => (
              <MenuItem key={member.D1Id} value={member.EmailId || member.emailId}>
                {member.TeamMemberName || member.teamMemberName}
              </MenuItem>
            ))}
          </Select>
          {errors.person && <Typography className="error-text">{errors.person}</Typography>}
        </Box>

        {/* Planned Date */}
        <Box onClick={(e) => e.stopPropagation()}>
          <TwoMonthDatePicker
            value={plannedDate}
            onChange={(value) => handlePlannedChange(value)}
            label="Planned Date"
            disabled={!effectiveEnabled || isFormBlocked || (isResponsibleSet && !isEditMode)}
            InputLabelProps={{ style: { fontSize: '0.8rem', color: '#777' } }}
          />
        </Box>

        {/* Achieved Date - enabled when responsible person is set */}
        <Box onClick={(e) => {
          e.stopPropagation();
          if (isResponsibleSet && !expanded) {
            setIsEditMode(true);
            setFormClosed(false);
            setIsFormTouched(false);
            onToggleAccordion();
          }
        }}>
          <TwoMonthDatePicker
            value={achievedDate}
            onChange={(d) => {
              setAchievedDate(d);
              setIsFormTouched(true);
              if (!isEditMode) {
                setIsEditMode(true);
                onToggleAccordion();
                setTimeout(() => { setIsFormTouched(false); }, 0);
              }
            }}
            label="Achieved Date"
            disabled={!isResponsibleSet || isFormBlocked}
            maxDate={new Date()}
            InputLabelProps={{ style: { fontSize: '0.8rem', color: isEditMode && !achievedDate ? '#d32f2f' : '#777' } }}
            sx={isEditMode && !achievedDate ? {
              '& .MuiInput-underline:before': { borderBottomColor: '#d32f2f' },
              '& .MuiInput-underline:after': { borderBottomColor: '#d32f2f' },
              '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: '#d32f2f' },
              '& .MuiInputLabel-root': { color: '#d32f2f !important' },
            } : {}}
          />
        </Box>

        {/* Edit/Update Buttons */}
        {isResponsibleSet ? (
          <Box className="filBtn float-left" onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              disabled={!effectiveEnabled || isFormBlocked}
              onClick={() => { setIsEditMode(true); setFormClosed(false); onToggleAccordion(); setIsFormTouched(false); }}
              sx={{ minWidth: '60px', fontSize: '0.75rem' }}
            >
              Edit
            </Button>
            <Button 
              variant="outlined" 
              disabled={!effectiveEnabled || isFormBlocked || !responsiblePerson || !plannedDate} 
              onClick={handleUpdateResponsiblePerson}
              sx={{ minWidth: '70px', fontSize: '0.75rem' }}
            >
              Update
            </Button>
          </Box>
        ) : (
          <Box className="filBtn float-left" onClick={(e) => e.stopPropagation()}>
            <Button variant="outlined" disabled={!effectiveEnabled || isFormBlocked || !responsiblePerson || !plannedDate} onClick={handleUpdateResponsiblePerson}>Set</Button>
          </Box>
        )}
      </Box>

      {/* Success/Error Messages */}
      {(saveNotice || saveError) && (
        <Box sx={{ position: 'fixed', top: 20, right: 20, zIndex: 9999, minWidth: '300px' }}>
          {saveError && <Alert severity="error" onClose={() => setSaveError(null)}>{saveError}</Alert>}
          {saveNotice && <Alert severity="success" onClose={() => setSaveNotice(null)}>{saveNotice}</Alert>}
        </Box>
      )}

      {/* Accordion Form */}
      {expanded && (isEditMode || readOnly) && (
        <Accordion expanded={expanded} sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box className="accordion-close-button-container">
              <Button
                className="close-button"
                variant="contained"
                color="secondary"
                startIcon={<CloseIcon />}
                onClick={() => { setIsEditMode(false); setFormClosed(true); handleCloseForm(); }}
              >
                Close Form
              </Button>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={isFormBlocked ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
            {/* Q1: Occurrence */}
            <Box className="question-block">
              <Box className="question-label">
                <span className="d2-qnum">Q1</span>
                <span className="d2-question-text">Occurrence</span>
              </Box>
              {occurrenceRows.map((row, index) => {
                const needsRoot = isFormTouched && rowHasInput(row) && !row.rootCause?.trim();
                const needsResp = isFormTouched && rowHasInput(row) && !row.responsible;
                const needsDate = isFormTouched && rowHasInput(row) && !row.date;
                return (
                <Box key={`occ-${index}`} className="q1-container">
                  <TextField
                    variant="standard"
                    label={needsRoot ? 'Root Cause *' : 'Root Cause'}
                    value={row.rootCause}
                    onChange={(e) => handleRowChange(occurrenceRows, setOccurrenceRows, index, 'rootCause', e.target.value)}
                    fullWidth
                    error={needsRoot}
                    InputLabelProps={{ style: { fontSize: '0.8rem', color: needsRoot ? '#d32f2f' : '#777' } }}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: needsRoot ? '#d32f2f' : '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: needsRoot ? '#d32f2f' : '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: needsRoot ? '#d32f2f' : '#800040',
                      }
                    }}
                  />
                  <TextField
                    variant="standard"
                    label="Action"
                    value={row.action}
                    onChange={(e) => handleRowChange(occurrenceRows, setOccurrenceRows, index, 'action', e.target.value)}
                    fullWidth
                    InputLabelProps={{ style: { fontSize: '0.8rem', color: '#777' } }}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      }
                    }}
                  />
                  <Select
                    value={row.responsible}
                    onChange={(e) => handleRowChange(occurrenceRows, setOccurrenceRows, index, 'responsible', e.target.value)}
                    displayEmpty
                    variant="standard"
                    error={needsResp}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: needsResp ? '#d32f2f' : '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: needsResp ? '#d32f2f' : '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: needsResp ? '#d32f2f' : '#800040',
                      },
                      '& .MuiSelect-select': {
                        color: needsResp ? '#d32f2f' : 'inherit',
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ display: 'none' }}>Responsible Person</MenuItem>
                    {row.responsible && !teamMembers.some(m => m.TeamMemberName === row.responsible) && (
                      <MenuItem value={row.responsible}>{row.responsible}</MenuItem>
                    )}
                    {teamMembers.map((member) => (
                      <MenuItem key={member.D1Id} value={member.TeamMemberName}>
                        {member.TeamMemberName}
                      </MenuItem>
                    ))}
                  </Select>

                  <TwoMonthDatePicker
                    value={row.date}
                    onChange={(date) => handleRowChange(occurrenceRows, setOccurrenceRows, index, 'date', date)}
                    label={needsDate ? 'Target Date *' : 'Target Date'}
                    error={needsDate}
                    InputLabelProps={{ style: { fontSize: '0.8rem', color: needsDate ? '#d32f2f' : '#777' } }}
                    sx={{
                      '& .MuiInput-underline:before': { borderBottomColor: needsDate ? '#d32f2f' : '#ccc' },
                      '& .MuiInput-underline:after': { borderBottomColor: needsDate ? '#d32f2f' : '#800040' },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: needsDate ? '#d32f2f' : '#800040' },
                    }}
                  />

                  <Select
                    value={row.status}
                    onChange={(e) => handleRowChange(occurrenceRows, setOccurrenceRows, index, 'status', e.target.value)}
                    displayEmpty
                    variant="standard"
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      }
                    }}
                  >
                    <MenuItem value="" disabled style={{ display: 'none' }}>Status</MenuItem>
                    <MenuItem value="Complete">Complete</MenuItem>
                    <MenuItem value="Inprocess">Inprocess</MenuItem>
                  </Select>

                  {index === occurrenceRows.length - 1 && occurrenceRows.length < maxRows ? (
                    <IconButton onClick={() => handleAddRow(occurrenceRows, setOccurrenceRows)} className="q1-add-btn">
                      <AddIcon />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => handleRemoveRow(occurrenceRows, setOccurrenceRows, index)} className="q1-remove-btn">
                      <RemoveIcon />
                    </IconButton>
                  )}
                </Box>
              );})}
            </Box>

            {/* Q2: Non Detection */}
            <Box className="question-block">
              <Box className="question-label">
                <span className="d2-qnum">Q2</span>
                <span className="d2-question-text">Non Detection</span>
              </Box>
              {nonDetectionRows.map((row, index) => {
                const needsRoot = isFormTouched && rowHasInput(row) && !row.rootCause?.trim();
                const needsResp = isFormTouched && rowHasInput(row) && !row.responsible;
                const needsDate = isFormTouched && rowHasInput(row) && !row.date;
                return (
                <Box key={`nd-${index}`} className="q1-container">
                  <TextField
                    variant="standard"
                    label={needsRoot ? 'Root Cause *' : 'Root Cause'}
                    value={row.rootCause}
                    onChange={(e) => handleRowChange(nonDetectionRows, setNonDetectionRows, index, 'rootCause', e.target.value)}
                    fullWidth
                    error={needsRoot}
                    InputLabelProps={{ style: { fontSize: '0.8rem', color: needsRoot ? '#d32f2f' : '#777' } }}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: needsRoot ? '#d32f2f' : '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: needsRoot ? '#d32f2f' : '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: needsRoot ? '#d32f2f' : '#800040',
                      }
                    }}
                  />
                  <TextField
                    variant="standard"
                    label="Action"
                    value={row.action}
                    onChange={(e) => handleRowChange(nonDetectionRows, setNonDetectionRows, index, 'action', e.target.value)}
                    fullWidth
                    InputLabelProps={{ style: { fontSize: '0.8rem', color: '#777' } }}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      }
                    }}
                  />
                  <Select
                    value={row.responsible}
                    onChange={(e) => handleRowChange(nonDetectionRows, setNonDetectionRows, index, 'responsible', e.target.value)}
                    displayEmpty
                    variant="standard"
                    error={needsResp}
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: needsResp ? '#d32f2f' : '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: needsResp ? '#d32f2f' : '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: needsResp ? '#d32f2f' : '#800040',
                      },
                      '& .MuiSelect-select': {
                        color: needsResp ? '#d32f2f' : 'inherit',
                      }
                    }}
                  >
                    <MenuItem value="" sx={{ display: 'none' }}>Responsible Person</MenuItem>
                    {row.responsible && !teamMembers.some(m => m.TeamMemberName === row.responsible) && (
                      <MenuItem value={row.responsible}>{row.responsible}</MenuItem>
                    )}
                    {teamMembers.map((member) => (
                      <MenuItem key={member.D1Id} value={member.TeamMemberName}>
                        {member.TeamMemberName}
                      </MenuItem>
                    ))}
                  </Select>

                  <TwoMonthDatePicker
                    value={row.date}
                    onChange={(date) => handleRowChange(nonDetectionRows, setNonDetectionRows, index, 'date', date)}
                    label={needsDate ? 'Target Date *' : 'Target Date'}
                    error={needsDate}
                    InputLabelProps={{ style: { fontSize: '0.8rem', color: needsDate ? '#d32f2f' : '#777' } }}
                    sx={{
                      '& .MuiInput-underline:before': { borderBottomColor: needsDate ? '#d32f2f' : '#ccc' },
                      '& .MuiInput-underline:after': { borderBottomColor: needsDate ? '#d32f2f' : '#800040' },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: needsDate ? '#d32f2f' : '#800040' },
                    }}
                  />

                  <Select
                    value={row.status}
                    onChange={(e) => handleRowChange(nonDetectionRows, setNonDetectionRows, index, 'status', e.target.value)}
                    displayEmpty
                    variant="standard"
                    sx={{
                      '& .MuiInput-underline:after': {
                        borderBottomColor: '#800040',
                      },
                      '& .MuiInput-underline:before': {
                        borderBottomColor: '#ccc',
                      },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#800040',
                      }
                    }}
                  >
                    <MenuItem value="" disabled style={{ display: 'none' }}>Status</MenuItem>
                    <MenuItem value="Complete">Complete</MenuItem>
                    <MenuItem value="Inprocess">Inprocess</MenuItem>
                  </Select>

                  {index === nonDetectionRows.length - 1 && nonDetectionRows.length < maxRows ? (
                    <IconButton onClick={() => handleAddRow(nonDetectionRows, setNonDetectionRows)} className="q1-add-btn">
                      <AddIcon />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => handleRemoveRow(nonDetectionRows, setNonDetectionRows, index)} className="q1-remove-btn">
                      <RemoveIcon />
                    </IconButton>
                  )}
                </Box>
              );})}
            </Box>

            {/* Attachments */}
            <ImageUpload onFilesSelected={(files) => {
              uploadedFiles.forEach(ef => {
                const efId = ef.AttachmentId || ef.attachmentId;
                if (efId && !files.some(f => (f.AttachmentId || f.attachmentId) === efId)) {
                  setAttachmentsToRemove(prev => prev.includes(efId) ? prev : [...prev, efId]);
                }
              });
              setUploadedFiles(files);
              setIsFormTouched(true);
            }} folderPath="D5" existingFiles={uploadedFiles} maxFiles={3} rejectedExtensions={['doc', 'docx']} />

            {/* Save & Reset Buttons */}
            <Box className="save-btn-container" sx={{ textAlign: 'center', mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button 
                variant="contained" 
                className="save-btn" 
                onClick={handleSave}
                disabled={saving || !isFormTouched || isFormBlocked}
                sx={{
                  backgroundColor: isFormTouched && !isFormBlocked ? '#800040' : '#ccc',
                  color: '#fff',
                  borderRadius: '20px',
                  textTransform: 'none',
                  px: 4,
                  cursor: isFormTouched ? 'pointer' : 'not-allowed'
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button 
                variant="contained" 
                onClick={handleReset}
                sx={{
                  backgroundColor: '#800040',
                  color: '#fff',
                  borderRadius: '20px',
                  textTransform: 'none',
                  px: 4
                }}
              >
                Reset
              </Button>
            </Box>

            {/* Save notices */}
            {saveError && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">{saveError}</Alert>
              </Box>
            )}
            {saveNotice && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="success">{saveNotice}</Alert>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

export default D5;
